import { cn, Textarea } from '@heroui/react'
import { useEffect, useRef, useState } from 'react'

/**
 * Component that renders a text editor with effect tags displayed as pills
 */
export default function TaggedTextEditor({
  value,
  onValueChange,
  placeholder,
  label,
  description,
  variant = 'bordered',
  radius = 'sm',
  color = 'default',
  supportedEffects = [],
  id,
}) {
  const [pillsVisible, setPillsVisible] = useState(true)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const textareaRef = useRef(null)
  const pillsContainerRef = useRef(null)
  const suggestionClickRef = useRef(false)

  // Parse the text and create an array of text segments and pills
  const parsedContent = () => {
    if (!value)
      return []

    const segments = []
    let lastIndex = 0
    const regex = /<(\w+)>/g

    let match = regex.exec(value)
    while (match !== null) {
      // Add text before the tag
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: value.substring(lastIndex, match.index),
        })
      }

      // Add the effect pill
      const effectName = match[1]
      const effect = supportedEffects.find(e => e.name === effectName)
      segments.push({
        type: 'pill',
        content: effectName,
        color: effect?.color || '#e0e7ff',
        textColor: effect?.textColor || '#4838e0',
      })

      lastIndex = match.index + match[0].length
      match = regex.exec(value)
    }

    // Add remaining text after the last tag
    if (lastIndex < value.length) {
      segments.push({
        type: 'text',
        content: value.substring(lastIndex),
      })
    }

    return segments
  }

  // Handle focus events to switch between pills view and raw text view
  const handleFocus = () => {
    setPillsVisible(false)
  }

  const handleBlur = () => {
    // Only show pills if we're not clicking on a suggestion
    if (!suggestionClickRef.current) {
      setPillsVisible(true)
      setShowSuggestions(false)
    }

    // Reset for next time
    suggestionClickRef.current = false
  }

  // Handle key events to detect typing '<'
  const handleKeyDown = (e) => {
    if (e.key === '<') {
      // Show suggestions after typing '<'
      setTimeout(() => {
        setShowSuggestions(true)
      }, 10)
    }
    else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  // Insert a suggested effect tag
  const insertEffectTag = (effectName) => {
    // Set flag to prevent blur handler from hiding the editor
    suggestionClickRef.current = true

    if (!textareaRef.current)
      return

    const startPos = textareaRef.current.selectionStart
    const beforeText = value.substring(0, startPos)
    const afterText = value.substring(startPos)

    // Check if we're in the middle of typing a tag
    const tagStart = beforeText.lastIndexOf('<')
    if (tagStart >= 0 && !beforeText.substring(tagStart).includes('>')) {
      // Replace partial tag with the full tag
      const newValue = `${beforeText.substring(0, tagStart)}<${effectName}>${afterText}`
      onValueChange(newValue)

      // Position cursor after the inserted tag
      setTimeout(() => {
        if (textareaRef.current) {
          const newPos = tagStart + effectName.length + 2 // +2 for < and >
          textareaRef.current.focus()
          textareaRef.current.setSelectionRange(newPos, newPos)
        }
      }, 10)
    }
    else {
      // Just insert the tag at cursor
      const newValue = `${beforeText}<${effectName}>${afterText}`
      onValueChange(newValue)

      // Position cursor after the inserted tag
      setTimeout(() => {
        if (textareaRef.current) {
          const newPos = startPos + effectName.length + 2 // +2 for < and >
          textareaRef.current.focus()
          textareaRef.current.setSelectionRange(newPos, newPos)
        }
      }, 10)
    }

    setShowSuggestions(false)
  }

  // Sync the height of textarea and pills container
  useEffect(() => {
    if (pillsContainerRef.current && textareaRef.current) {
      pillsContainerRef.current.style.minHeight = `${textareaRef.current.offsetHeight}px`
    }
  }, [value, pillsVisible])

  return (
    <div className="relative">
      {label && (
        <label className="font-medium text-sm mb-2 block">
          {label}
        </label>
      )}
      {/* The actual textarea (hidden when showing pills) */}
      <Textarea
        ref={textareaRef}
        id={id}
        value={value}
        onValueChange={onValueChange}
        placeholder={placeholder}
        variant={variant}
        radius={radius}
        color="primary"
        className={cn(pillsVisible && 'opacity-0 absolute inset-0', 'font-mono !text-xs text-gray-500')}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />

      {/* Pills view (shown when not editing) */}
      {pillsVisible && (
        <div
          ref={pillsContainerRef}
          className={cn(
            'min-h-[80px] p-2 border rounded overflow-y-auto font-mono text-gray-500 text-sm whitespace-pre-wrap',
            `border-${color}-500`,
            `rounded-${radius}`,
          )}
          onClick={handleFocus}
        >
          {value
            ? (
                parsedContent().map((segment, index) =>
                  segment.type === 'text'
                    ? (
                        <span key={index}>{segment.content}</span>
                      )
                    : (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded-sm text-sm font-medium mx-0.5"
                          style={{ backgroundColor: segment.color, color: segment.textColor }}
                        >
                          {segment.content}
                        </span>
                      ),
                )
              )
            : (
                <span className="text-gray-400">{placeholder}</span>
              )}
        </div>
      )}

      {/* Effect suggestions dropdown */}
      {showSuggestions && !pillsVisible && (
        <div className="absolute z-20 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {supportedEffects.map(effect => (
            <div
              key={effect.name}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
              onClick={() => insertEffectTag(effect.name)}
              onMouseDown={e => e.preventDefault()} // Prevent blur before click
            >
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium mr-2"
                style={{ backgroundColor: effect.color, color: effect.textColor }}
              >
                {effect.name}
              </span>
              <span className="text-xs text-gray-500">{effect.description}</span>
            </div>
          ))}
        </div>
      )}

      {description && !pillsVisible && (
        <div className="text-xs text-gray-500 mt-1">
          {description}
        </div>
      )}
    </div>
  )
}
